import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Sparkles, Download, Film } from "lucide-react";
import axios from "axios";
import { kindOf, genDownload, brand } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Share() {
  const { token } = useParams();
  const [item, setItem] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/share/${token}`)
      .then(r => setItem(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "Not found"))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <Shell><div className="text-zinc-500 font-mono text-sm">loading preview…</div></Shell>;
  if (err || !item) return <Shell><div className="text-zinc-400 text-sm">{err || "This share link is invalid or has been disabled."}</div></Shell>;

  const isVideo = kindOf(item) === "video";
  const isAudio = kindOf(item) === "audio";
  const raw = item.output_urls?.[0];
  const src = raw && raw.startsWith("/api/") ? `${BACKEND_URL}${raw}` : raw;

  return (
    <Shell>
      <div className="w-full max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6 ax-fade-up">
        <div className="lg:col-span-2 ax-card overflow-hidden" data-testid="share-media">
          <div className="min-h-[70vh] max-h-[80vh] bg-black flex items-center justify-center">
            {src ? (
              isVideo
                ? <video src={src} controls autoPlay className="max-w-full max-h-[80vh]"/>
                : isAudio
                  ? <div className="w-full flex flex-col items-center gap-6 p-10"><Film className="w-16 h-16 text-zinc-700"/><audio src={src} controls className="w-11/12"/></div>
                  : <img src={src} alt="" className="max-w-full max-h-[80vh] object-contain"/>
            ) : <div className="text-zinc-600">no output</div>}
          </div>
        </div>
        <aside className="ax-card p-6 flex flex-col gap-5" data-testid="share-meta">
          <div className="ax-pill self-start">Shared preview</div>
          <div>
            <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest">Model</div>
            <div className="font-heading text-xl font-semibold mt-1">{brand(item.model_name)}</div>
          </div>
          <div>
            <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest">Prompt</div>
            <p className="text-sm text-zinc-200 mt-1 leading-relaxed whitespace-pre-wrap">{item.prompt || "—"}</p>
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <KV k="Category" v={item.category}/>
            <KV k="Created" v={new Date(item.created_at).toLocaleString()}/>
            {item.settings?.duration && <KV k="Duration" v={`${item.settings.duration}s`}/>}
            {item.settings?.aspect_ratio && <KV k="Aspect" v={item.settings.aspect_ratio}/>}
            {item.settings?.resolution && <KV k="Resolution" v={item.settings.resolution}/>}
            {item.settings?.quality && <KV k="Quality" v={item.settings.quality}/>}
            {item.settings?.num_images && <KV k="Outputs" v={item.settings.num_images}/>}
            {item.mode && <KV k="Mode" v={item.mode.replace(/_/g, " ")}/>}
            {item.cost_usd != null && <KV k="Cost" v={`$${Number(item.cost_usd).toFixed(3)}`}/>}
          </div>
          {item.output_urls?.length > 1 && (
            <div className="grid grid-cols-4 gap-2" data-testid="share-outputs">
              {item.output_urls.map((u, i) => <a key={u} href={genDownload(item, i, token)} className="aspect-square rounded-md overflow-hidden border border-zinc-800"><img src={u} alt={`output ${i + 1}`} className="w-full h-full object-cover"/></a>)}
            </div>
          )}
          {src && (
            <a href={genDownload(item, 0, token)}
               data-testid="share-download"
               className="h-11 rounded-md flex items-center justify-center text-sm font-semibold btn-primary-glow">
              <Download className="w-4 h-4 mr-2"/> Download original
            </a>
          )}
        </aside>
      </div>
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div className="min-h-screen relative z-[1] bg-transparent text-[#F5F5F7]">
    <header className="h-16 border-b border-zinc-800/80 flex items-center px-6">
      <div className="h-9 w-9 rounded-xl btn-primary-glow flex items-center justify-center">
        <Sparkles className="w-4 h-4 text-white"/>
      </div>
      <div className="ml-3 font-heading font-bold tracking-tight">Ai Xtudio · Preview</div>
    </header>
    <main className="px-6 py-10 flex items-center justify-center">{children}</main>
  </div>
);

const KV = ({ k, v }) => (
  <div>
    <div className="text-[10px] font-mono text-zinc-600 uppercase tracking-widest">{k}</div>
    <div className="text-sm text-zinc-300 mt-0.5 break-words">{v}</div>
  </div>
);
